import type { SupabaseClient } from '@supabase/supabase-js';
import { ImageCacheService } from './imageCacheService';
import { dataURLToBlob } from './imageUtils';
import { uploadItemImage, deleteItemImage, requireWrittenRows } from './supabaseService';
import { invalidateUserData } from '../src/lib/queryClient';

export const MAX_RECEIPT_BYTES = 10 * 1024 * 1024;
export const RECEIPT_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif', 'image/heic', 'image/heif'];
export type ReceiptAttachmentErrorCode = 'invalid_image' | 'too_large' | 'unreadable' | 'save_failed';
export class ReceiptAttachmentError extends Error {
  constructor(public code: ReceiptAttachmentErrorCode) { super(code); }
}

/** Attachment only: retain the original image; never invoke extraction or grounding. */
export async function validateReceiptPhoto(file: File): Promise<void> {
  if (!RECEIPT_IMAGE_TYPES.includes(file.type) || !file.size) throw new ReceiptAttachmentError('invalid_image');
  if (file.size > MAX_RECEIPT_BYTES) throw new ReceiptAttachmentError('too_large');
  const url = URL.createObjectURL(file);
  try {
    await new Promise<void>((resolve, reject) => {
      const image = new Image();
      image.onload = () => image.naturalWidth && image.naturalHeight ? resolve() : reject(new ReceiptAttachmentError('unreadable'));
      image.onerror = () => reject(new ReceiptAttachmentError('unreadable'));
      image.src = url;
    });
  } finally { URL.revokeObjectURL(url); }
}

export async function stageReceiptPhoto(file: File): Promise<string> {
  const url = await ImageCacheService.saveImage(file);
  // Don't persist an ephemeral blob URL if IndexedDB failed; preserve the old receipt.
  if (!url.startsWith('local:')) {
    if (url.startsWith('blob:')) URL.revokeObjectURL(url);
    throw new ReceiptAttachmentError('save_failed');
  }
  return url;
}

/** A selected receipt is required to reach Storage before the trip may be archived. */
export async function uploadBasketReceipt(supabase: SupabaseClient, userId: string, imageUrl: string | null): Promise<string | null> {
  if (!imageUrl) return null;
  if (/^https?:\/\//.test(imageUrl)) return imageUrl;
  const existing = await ImageCacheService.getCloudUrl(imageUrl);
  if (existing) return existing;
  const blob = imageUrl.startsWith('data:') ? dataURLToBlob(imageUrl)
    : imageUrl.startsWith('blob:') ? await fetch(imageUrl).then(response => {
      if (!response.ok) throw new ReceiptAttachmentError('save_failed');
      return response.blob();
    }) : await ImageCacheService.getImageBlob(imageUrl);
  if (!blob) throw new ReceiptAttachmentError('save_failed');
  const uploaded = await uploadItemImage(supabase, userId, blob, 'checkout-receipt');
  await ImageCacheService.registerCloudUrl(imageUrl, uploaded);
  return uploaded;
}

/** Checkout photos have a distinct filename, including receipts uploaded from History. */
export function isCheckoutReceiptAttachment(imageUrl?: string | null): boolean {
  if (!imageUrl) return false;
  try { return /\/items\/[^/]+\/[^/]+_checkout-receipt(?:\.[a-z0-9]+)?$/i.test(new URL(imageUrl).pathname); }
  catch { return false; }
}

/** Attach/replace only the receipt the caller saw on their own personal trip. */
export async function attachReceiptToHistory(supabase: SupabaseClient, userId: string, historyId: string, file: File, previousReceiptUrl: string | null = null): Promise<string> {
  if (previousReceiptUrl && !isCheckoutReceiptAttachment(previousReceiptUrl)) throw new ReceiptAttachmentError('save_failed');
  const uploaded = await uploadItemImage(supabase, userId, file, 'checkout-receipt');
  let result: { data: { id: string }[] | null; error: { code?: string; message?: string } | null } | undefined;
  try {
    const query = supabase.from('shopping_history')
      .update({ receipt_image_url: uploaded })
      .eq('id', historyId).eq('user_id', userId).eq('is_personal_trip', true);
    result = await (previousReceiptUrl === null
      ? query.is('receipt_image_url', null)
      : query.eq('receipt_image_url', previousReceiptUrl)).select('id');
    requireWrittenRows(result, 'Attach checkout receipt');
  } catch (error) {
    // Clean up only a definite refusal. A lost network response may hide a
    // committed write, so keep that photo rather than risk breaking its reference.
    const noRows = result && !result.error && Array.isArray(result.data) && result.data.length === 0;
    const databaseRefusal = /^\d{5}$/.test(result?.error?.code || '');
    if (noRows || databaseRefusal) await deleteItemImage(supabase, uploaded, userId);
    throw error;
  }
  void invalidateUserData(userId);
  if (previousReceiptUrl && previousReceiptUrl !== uploaded) {
    await deleteItemImage(supabase, previousReceiptUrl, userId);
  }
  return uploaded;
}

/** Clear the attachment first; failed/stale writes must never delete its photo. */
export async function removeReceiptFromHistory(supabase: SupabaseClient, userId: string, historyId: string, previousReceiptUrl: string): Promise<void> {
  if (!isCheckoutReceiptAttachment(previousReceiptUrl)) throw new ReceiptAttachmentError('save_failed');
  const result = await supabase.from('shopping_history')
    .update({ receipt_image_url: null })
    .eq('id', historyId).eq('user_id', userId).eq('is_personal_trip', true)
    .eq('receipt_image_url', previousReceiptUrl).select('id');
  requireWrittenRows(result, 'Remove checkout receipt');
  void invalidateUserData(userId);
  await deleteItemImage(supabase, previousReceiptUrl, userId);
}
